import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';

export interface UpcomingEvent {
  time: number; // timestamp in ms
  event: string;
  country: string;
  importance: 'high' | 'medium' | 'low';
  category?: 'economic' | 'earnings';
}

interface EventRendererData {
  x: number;
  event: UpcomingEvent;
}

class UpcomingEventsPaneRenderer implements IPrimitivePaneRenderer {
  private _data: EventRendererData[] = [];
  private _bottomOffset: number = 45;

  update(data: EventRendererData[], bottomOffset: number): void {
    this._data = data;
    this._bottomOffset = bottomOffset;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const bitmapHeight = scope.bitmapSize.height as number;

      // Position at bottom, same area as news markers
      const markerY = bitmapHeight - (this._bottomOffset * pixelRatio);

      for (const item of this._data) {
        const x = item.x * pixelRatio;

        // Draw vertical dashed line from top to marker
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3 * pixelRatio, 3 * pixelRatio]);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, markerY - 10 * pixelRatio);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();

        // Draw the event marker: 📊 for earnings, ⭐ for economic events.
        ctx.font = `${14 * pixelRatio}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.event.category === 'earnings' ? '📊' : '⭐', x, markerY);

        // Short date on first line, time on second, countdown on third
        ctx.font = `${8 * pixelRatio}px Arial`;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.textBaseline = 'top';

        const { dateStr, timeStr } = this.formatDateTime(item.event.time);
        ctx.fillText(dateStr, x, markerY + 10 * pixelRatio);
        ctx.fillText(timeStr, x, markerY + 19 * pixelRatio);
        ctx.fillText(this.formatCountdown(item.event.time), x, markerY + 28 * pixelRatio);
      }
    });
  }

  /**
   * How far off the event is, as a glanceable label.
   *
   * Hours rather than days even for distant events: the date is already on the
   * line above, so hours answers the question the date does not.
   *
   * Never renders a negative. setEvents() filters on `e.time > now` at the
   * moment it is called, so an event can fall due while still on screen.
   */
  private formatCountdown(timestamp: number): string {
    const remainingMs = timestamp - Date.now();
    const minutes = Math.floor(remainingMs / 60_000);

    // Under a minute reads as 'now' rather than 'in 0m'.
    if (minutes < 1) return 'now';
    if (minutes < 60) return `in ${minutes}m`;

    const hours = Math.floor(minutes / 60);
    return `in ${hours}${hours === 1 ? 'hr' : 'hrs'}`;
  }

  private formatDateTime(timestamp: number): { dateStr: string, timeStr: string } {
    const date = new Date(timestamp);
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Los_Angeles'
    }).toLowerCase();
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Los_Angeles'
    });
    return { dateStr, timeStr };
  }
}

class UpcomingEventsPaneView implements IPrimitivePaneView {
  private _source: UpcomingEventsPrimitive;
  private _renderer: UpcomingEventsPaneRenderer;

  constructor(source: UpcomingEventsPrimitive) {
    this._source = source;
    this._renderer = new UpcomingEventsPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    this._renderer.update(data, this._source.getBottomOffset());
    return this._renderer;
  }
}

export class UpcomingEventsPrimitive implements ISeriesPrimitive<Time> {
  private _events: UpcomingEvent[] = [];
  private _paneView: UpcomingEventsPaneView;
  private _requestUpdate?: () => void;
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _rendererData: EventRendererData[] = [];
  private _lastCandleTime: number = 0;
  private _bottomOffset: number = 45; // Same area as news markers
  private _countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this._paneView = new UpcomingEventsPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;

    // Without this the countdown freezes at whatever it read on the last
    // repaint. Repaints come only from setEvents() and setLastCandleTime(), so
    // a quiet or closed market produces none at all — leaving a stale
    // 'in 3hrs' on screen looking live, which is worse than showing nothing.
    // A minute is ample for hour/minute granularity.
    this._countdownTimer = setInterval(() => this._requestUpdate?.(), 60_000);
  }

  detached(): void {
    if (this._countdownTimer !== null) {
      clearInterval(this._countdownTimer);
      this._countdownTimer = null;
    }
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {}

  setEvents(events: UpcomingEvent[]): void {
    const now = Date.now();
    this._events = events
      .filter(e => e.time > now)
      .sort((a, b) => a.time - b.time)
      .slice(0, 3);
    this._requestUpdate?.();
  }

  setLastCandleTime(time: number): void {
    this._lastCandleTime = time;
    this._requestUpdate?.();
  }

  getBottomOffset(): number {
    return this._bottomOffset;
  }

  checkClick(clickX: number, clickY: number, chartHeight: number): UpcomingEvent | null {
    const hitRadius = 30;
    const markerY = chartHeight - this._bottomOffset;

    for (const item of this._rendererData) {
      const dx = Math.abs(clickX - item.x);
      const dy = Math.abs(clickY - markerY);

      if (dx <= hitRadius && dy <= hitRadius) {
        return item.event;
      }
    }

    return null;
  }

  getRendererData(): EventRendererData[] {
    if (!this._chart || this._events.length === 0) {
      return [];
    }

    const timeScale = this._chart.timeScale();
    const chartWidth = timeScale.width();

    // Get x position for the last candle
    let lastCandleX: number;

    if (this._lastCandleTime > 0) {
      const x = timeScale.timeToCoordinate(this._lastCandleTime as Time);
      lastCandleX = x !== null ? x : chartWidth - 50;
    } else {
      lastCandleX = chartWidth - 50;
    }

    const data: EventRendererData[] = [];

    // Space events apart - user can scroll to see more
    const spacing = 80;
    const startOffset = 50; // Start after last candle

    this._events.forEach((event, index) => {
      const x = lastCandleX + startOffset + index * spacing;

      // Include even if off-screen right (user can scroll)
      if (x > -50) {
        data.push({ x, event });
      }
    });

    this._rendererData = data;
    return data;
  }
}
